import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  TextField,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  Button,
  Paper,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { animateScroll as scroll } from 'react-scroll';
import withRouter from '~/withRouter/withRouter.component';
import { useFielderSnackBar } from '~/FielderElements/useFielderSnackBar';
import FielderLogoImage from '@assets/icons/logo-login-icon.svg';
import FielderLogoNegative from '@assets/icons/logo-login-icon-light.svg';
import {
  setSelectedCognitoProvider,
  getSelectedCognitoProvider,
  removeSelectedCognitoProvider,
} from 'FielderUtils/session/Session';

const AD_PROVIDERS = {
  smart: {
    id: process.env.COGNITO_IDENTITY_PROVIDER_SMART,
    name: 'Smart',
  },
  testLocal: {
    id: process.env.COGNITO_IDENTITY_PROVIDER,
    name: 'Test Local Cognito',
  },
  camsTech: {
    id: process.env.COGNITO_IDENTITY_PROVIDER_CAMS,
    name: 'Cams Tech',
  },
  StefaniniMexico: {
    id: process.env.COGNITO_IDENTITY_PROVIDER_STEFANINI_MX,
    name: 'Stefanini México',
  },
};

const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(2),
}));

const FormPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  maxWidth: 460,
  padding: theme.spacing(5),
  borderRadius: theme.shape.borderRadius * 2,
  textAlign: 'center',
  backgroundColor: theme.palette.background.paper,
}));

const Logo = styled('img')(({ theme }) => ({
  display: 'block',
  margin: '0 auto',
  marginBottom: theme.spacing(5),
  maxWidth: 140,
  height: 'auto',
  objectFit: 'contain',
}));

const CognitoProvider = ({ lan, navigate, fielderMsg }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const logoSrc = isDarkMode ? FielderLogoNegative : FielderLogoImage;

  const [provider, setProvider] = useState('');
  const [showProvider, setShowProvider] = useState(false);

  useEffect(() => {
    scroll.scrollToBottom();

    const savedProvider = getSelectedCognitoProvider();
    if (savedProvider) {
      try {
        const parsedProvider = JSON.parse(savedProvider);
        setProvider(parsedProvider.id);
      } catch (error) {
        console.error('Error parsing stored provider:', error);
        removeSelectedCognitoProvider();
      }
    }
  }, []);

  const showError = () => {
    fielderMsg({
      message: lan.validProviderName,
      variant: 'error',
      closeButton: true,
      duration: 3000,
    });
  };

  const handleBack = () => {
    navigate('/login');
  };

  const handleContinue = () => {
    if (!provider) {
      showError();
      return;
    }

    const selectedProvider = Object.values(AD_PROVIDERS).find(
      (p) => p.id === provider,
    );

    if (!selectedProvider) {
      showError();
      return;
    }

    setSelectedCognitoProvider(JSON.stringify(selectedProvider));

    fielderMsg({
      message: lan.successProviderName,
      variant: 'success',
      closeButton: true,
      duration: 3000,
    });

    handleBack();
  };

  return (
    <PageContainer>
      <FormPaper elevation={1}>
        <Logo src={logoSrc} alt="Fielder logo" />

        <Typography
          variant="h5"
          gutterBottom
          sx={{ fontWeight: 600, mb: 1 }}
        >
          {lan.selectSignInProvider}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom
          sx={{ mb: 3 }}
        >
          {lan.useSignInProvider}
        </Typography>

        <TextField
          fullWidth
          label={lan.providerName}
          variant="outlined"
          margin="normal"
          type={showProvider ? 'text' : 'password'}
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowProvider(prev => !prev)}
                    edge="end"
                    aria-label="toggle provider visibility"
                  >
                    {showProvider ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        <Box mt={3}>
          <Grid
            container
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Grid size="auto">
              <Button
                id="btn-provider-back"
                variant="outlined"
                color="primary"
                size="large"
                onClick={handleBack}
                sx={{ minWidth: 120 }}
              >
                {lan.back}
              </Button>
            </Grid>
            <Grid size="auto">
              <Button
                id="btn-provider-continue"
                variant="contained"
                color="primary"
                size="large"
                onClick={handleContinue}
                sx={{ minWidth: 140 }}
              >
                {lan.selectedProvider}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </FormPaper>
    </PageContainer>
  );
};
function withSnackbar(Component) {
  return function WrappedComponent(props) {
    const fielderMsg = useFielderSnackBar();
    return <Component {...props} fielderMsg={fielderMsg} />;
  };
}
export default connect()(withRouter(withSnackbar(CognitoProvider)));
//Original code:
/*import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { TextField, Box, Typography, IconButton, InputAdornment, Button } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { animateScroll as scroll } from 'react-scroll';
import withRouter from '~/withRouter/withRouter.component';
import { useFielderSnackBar } from '~/FielderElements/useFielderSnackBar';
import FielderLogoImage from '@assets/icons/logo-login-icon.svg';
import FielderLogoNegative from '@assets/icons/logo-login-icon-light.svg';
import {
  setSelectedCognitoProvider,
  getSelectedCognitoProvider,
  removeSelectedCognitoProvider,
} from 'FielderUtils/session/Session';

const PageContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
});
const FormContainer = styled(Box)({
  margin: 'auto',
  padding: '50px',
  textAlign: 'center',
  borderRadius: '8px',
  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
});
const Logo = styled('img')({
  width: 'auto',
  marginBottom: '50px',
});
const CognitoProvider = ({ lan, navigate, dispatch, fielderMsg }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const logoSrc = isDarkMode ? FielderLogoNegative : FielderLogoImage;
  const [provider, setProvider] = useState('');
  const [showProvider, setShowProvider] = useState(false);
  const [adProviders] = useState({
    "smart": {
      "id": process.env.COGNITO_IDENTITY_PROVIDER_SMART,
      "name": "Smart",
    },
    "testLocal": {
      "id": process.env.COGNITO_IDENTITY_PROVIDER,
      "name": "Test Local Cognito",
    },
    "camsTech": {
      "id": process.env.COGNITO_IDENTITY_PROVIDER_CAMS,
      "name": "Cams Tech",
    },
    "StefaniniMexico": {
      "id": process.env.COGNITO_IDENTITY_PROVIDER_STEFANINI_MX,
      "name": "Stefanini México",
    }
  });
  useEffect(() => {
    scroll.scrollToBottom();
    const savedProvider = getSelectedCognitoProvider();
    if (savedProvider) {
      try {
        const parsedProvider = JSON.parse(savedProvider);
        setProvider(parsedProvider.id);
      } catch (error) {
        console.error("Error parsing stored provider:", error);
        removeSelectedCognitoProvider();
      }
    }
  }, []);
  const handleContinue = () => {
    if (!provider) {
      fielderMsg({
        message: lan.validProviderName,
        variant: 'error',
        closeButton: true,
        duration: 3000,
      });
      return;
    }
    const selectedProvider =
      Object.values(adProviders).find((p) => p.id === provider);
    if (!selectedProvider) {
      fielderMsg({
        message: lan.validProviderName,
        variant: 'error',
        closeButton: true,
        duration: 3000,
      });
      return;
    }
    setSelectedCognitoProvider(JSON.stringify(selectedProvider));
    fielderMsg({
      message: lan.successProviderName,
      variant: 'success',
      closeButton: true,
      duration: 3000,
    });
    handleBack();
  };
  const handleBack = () => {
    navigate('/login');
  };
  return (
    <PageContainer>
      <FormContainer>
        <Logo src={logoSrc} alt="Fielder Logo" />
        <Typography variant="h5" gutterBottom>
          {lan.selectSignInProvider}
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {lan.useSignInProvider}
        </Typography>
        <TextField
          fullWidth
          label={lan.providerName}
          variant="outlined"
          margin="normal"
          type={showProvider ? "text" : "password"}
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowProvider(!showProvider)} edge="end">
                  {showProvider ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box mt={2}>
          <Grid container direction="row" sx={{ justifyContent: "space-between", alignItems: "center", }}>
            <Grid size={'auto'}>
              <Button
                id={"btn-provider-back"}
                variant="outlined"
                onClick={handleBack}
                fullWidth
                size={"large"}>
                {lan.back}
              </Button>
            </Grid>
            <Grid size={'auto'}>
              <Button
                variant="contained"
                id={"btn-provider-continue"}
                onClick={handleContinue}
                fullWidth size={"large"}>
                {lan.selectedProvider}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </FormContainer>
    </PageContainer>
  );
};
export default connect()(withRouter(withSnackbar(CognitoProvider)));
function withSnackbar(Component) {
  return function WrappedComponent(props) {
    const fielderMsg = useFielderSnackBar();
    return <Component {...props} fielderMsg={fielderMsg} />;
  };
}
*/
